import { useEffect, useState } from "react";

const STORAGE_KEY = "filteredCategoryIds";

/**
 * Custom hook to manage filtered category IDs with localStorage persistence
 * @param {Array} allCategories - All available categories
 * @returns {Object} - { filteredCategoryIds, setFilteredCategoryIds, initializeFilter }
 */
export const useCategoryFilter = (allCategories = []) => {
  const [filteredCategoryIds, setFilteredCategoryIds] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error(
        "Error loading filtered categories from localStorage:",
        error,
      );
      return [];
    }
  });

  // Initialize filter when categories are first loaded
  useEffect(() => {
    if (allCategories.length > 0 && filteredCategoryIds.length === 0) {
      // If no filter is set, select all categories by default
      const allIds = allCategories.map((cat) => cat.id);
      setFilteredCategoryIds(allIds);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(allIds));
    }
  }, [allCategories.length]);

  // Clean up filter when categories change (remove deleted categories)
  useEffect(() => {
    if (allCategories.length > 0 && filteredCategoryIds.length > 0) {
      const validCategoryIds = allCategories.map((cat) => cat.id);
      const cleanedFiltered = filteredCategoryIds.filter((id) =>
        validCategoryIds.includes(id),
      );

      // Only update if something was removed
      if (cleanedFiltered.length !== filteredCategoryIds.length) {
        setFilteredCategoryIds(cleanedFiltered);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cleanedFiltered));
      }
    }
  }, [allCategories, filteredCategoryIds]);

  // Save to localStorage whenever filter changes
  const updateFilteredCategoryIds = (newIds) => {
    setFilteredCategoryIds(newIds);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(newIds));
  };

  // Function to manually initialize or reset filter
  const initializeFilter = (categoryIds = []) => {
    const ids =
      categoryIds.length > 0 ? categoryIds : allCategories.map((cat) => cat.id);
    setFilteredCategoryIds(ids);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  };

  return {
    filteredCategoryIds,
    setFilteredCategoryIds: updateFilteredCategoryIds,
    initializeFilter,
  };
};

export default useCategoryFilter;
